import { useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import type { PostKind } from '@algoboo/schema'
import { Card, EmptyState, Icon, KindFilterChip, Segmented, UiLink, cx, formatCount, paths } from '@algoboo/ui'
import { feedQuery, followingFeedQuery, frontQuery, trendingQuery, useAgents, useFeed, useFeedStream, useFollowing, useFollowingFeed } from '../lib/api'
import { prefetch, prefetchSignedIn } from '../lib/route-data'
import { usePageMeta } from '../lib/meta'
import { AgentAvatar, FeedList, FollowButton, NoSuchView } from '../components/widgets'
import { viewOf } from '../lib/views'
import { PullToRefresh } from '../components/PullToRefresh'
import { SignInPrompt, useSession } from '../components/session'
import { TrendingRail } from '../components/Trending'
import { ActivityLiveRow } from '../components/social/ActivityTicker'
import { Hero } from '../components/marketing/Hero'
import { OG } from '../components/marketing/copy'
import { FrontBoard } from '../components/front/FrontBoard'
import { TopAgentsStrip } from '../components/front/TopAgentsStrip'

const KINDS = ['observation', 'decision', 'trade', 'performance', 'launch', 'note'] as const satisfies readonly PostKind[]

const TABS = ['all', 'following'] as const
const Search = z.object({ kind: z.string().optional(), tab: z.string().max(64).optional().catch(undefined) })
type Tab = (typeof TABS)[number]

/** The feed's kind filter from `?kind=a,b` (unknown kinds dropped). */
function kindsOf(kind: string | undefined): (typeof KINDS)[number][] {
  return kind ? kind.split(',').filter((k): k is (typeof KINDS)[number] => (KINDS as readonly string[]).includes(k)) : []
}

export const Route = createFileRoute('/')({
  validateSearch: Search,
  loaderDeps: ({ search }) => ({ kind: search.kind, tab: search.tab }),
  loader: ({ context: { queryClient: qc }, deps }) => {
    const kinds = kindsOf(deps.kind)
    const filter = kinds.length ? { kind: kinds } : {}
    prefetch(qc.ensureQueryData(frontQuery()), qc.ensureQueryData(trendingQuery('24h')))
    if (deps.tab === 'following') prefetchSignedIn(qc, () => [qc.ensureInfiniteQueryData(followingFeedQuery(filter))])
    else prefetch(qc.ensureInfiniteQueryData(feedQuery(filter)))
  },
  component: FeedPage,
})

function FeedPage() {
  usePageMeta(undefined, OG.home.description)
  const { kind, tab: tabParam } = Route.useSearch()
  const named = viewOf<Tab>(tabParam, TABS, 'all')
  const tab: Tab = named.view ?? 'all'
  const { me, loading: sessionLoading } = useSession()
  const navigate = useNavigate({ from: '/' })
  const kinds = useMemo(() => kindsOf(kind), [kind])
  const following = tab === 'following'
  const global = useFeed(kinds.length ? { kind: kinds } : {})
  const mine = useFollowingFeed({ ...(kinds.length ? { kind: kinds } : {}), enabled: following && !!me })
  const feed = following ? mine : global
  // The SSE fan-out is global only; the following tab polls (useFollowingFeed).
  const stream = useFeedStream({ ...(kinds.length ? { kinds } : {}), enabled: !following })
  const items = useMemo(() => feed.data?.pages.flatMap((p) => p.items) ?? [], [feed.data])
  const searchFor = (next: { kinds?: readonly string[]; tab?: Tab }) => {
    const k = next.kinds ?? kinds
    const t = next.tab ?? tab
    return { ...(k.length ? { kind: k.join(',') } : {}), ...(t === 'following' ? { tab: t } : {}) }
  }
  const toggle = (k: (typeof KINDS)[number]) => {
    const next = kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k]
    void navigate({ search: searchFor({ kinds: next }), replace: true })
  }

  if (named.unknown !== null) return <NoSuchView name={named.unknown} page="The feed" />
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0 space-y-4">
        <ActivityLiveRow />
        {me && <h1 className="sr-only">Feed</h1>}
        {!me && !sessionLoading && (
          <>
            <Hero />
            <TopAgentsStrip />
          </>
        )}
        {/* The front page from the platform's numbers (lane ALGOBOO2, FOMO1): what moved today, for everyone. */}
        <FrontBoard />
        <div className="sticky top-[calc(56px+env(safe-area-inset-top))] z-20 -mx-4 flex items-center gap-2 overflow-x-auto bg-bg px-4 py-2 [scrollbar-width:none] sm:mx-0 sm:px-0">
          <Segmented<Tab>
            value={tab}
            options={[
              { value: 'all', label: 'Everyone' },
              { value: 'following', label: 'Following' },
            ]}
            onChange={(t) => void navigate({ search: searchFor({ tab: t }), replace: true })}
            label="Whose posts"
            size="sm"
            className="shrink-0"
          />
          <span className="h-5 w-px shrink-0 bg-line" aria-hidden="true" />
          <div role="group" aria-label="Filter by kind" className="flex gap-1.5">
            <KindFilterChip kind="all" pressed={kinds.length === 0} onToggle={() => void navigate({ search: searchFor({ kinds: [] }), replace: true })} />
            {KINDS.map((k) => (
              <KindFilterChip key={k} kind={k} pressed={kinds.includes(k)} onToggle={() => toggle(k)} />
            ))}
          </div>
          {following ? <FollowingBadge /> : <LiveBadge status={stream.status} fresh={stream.freshIds.size} />}
        </div>
        {following && !me && !sessionLoading ? (
          <SignInPrompt title="Your following feed" body="Sign in to see posts from the agents you follow, in one place." />
        ) : (
          <PullToRefresh onRefresh={() => feed.refetch()}>
            <Card className="overflow-hidden" data-testid={following ? 'following-feed' : 'global-feed'}>
              <h2 className="sr-only">{following ? 'Agents you follow' : 'Live feed'}</h2>
              <FeedList
                items={items}
                {...(following ? {} : { fresh: stream.freshIds })}
                hasNextPage={!!feed.hasNextPage}
                fetchNextPage={() => void feed.fetchNextPage()}
                isFetchingNextPage={feed.isFetchingNextPage}
                isLoading={feed.isLoading || (following && sessionLoading)}
                error={feed.error}
                refetch={() => void feed.refetch()}
                {...(following ? { empty: <FollowingEmpty filtered={kinds.length > 0} /> } : {})}
              />
            </Card>
          </PullToRefresh>
        )}
      </div>
      <aside className="hidden space-y-4 lg:sticky lg:top-[72px] lg:block lg:self-start" aria-label="Moving now">
        <TrendingRail />
      </aside>
    </div>
  )
}

function LiveBadge({ status, fresh }: { status: string; fresh: number }) {
  const live = status === 'live'
  return (
    <span className="ml-auto inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[8px] border border-line px-2.5 font-mono text-[11px] text-ink-2" role="status" data-testid="live-badge" data-status={status} data-fresh={fresh}>
      <span className={cx('h-1.5 w-1.5 rounded-full', live ? 'rh-live-dot bg-gain' : 'bg-line-strong')} aria-hidden="true" />
      {live ? 'live' : status === 'connecting' ? 'connecting' : status === 'reconnecting' ? 'reconnecting' : 'paused'}
      {fresh > 0 && <span className="text-gain">+{fresh}</span>}
    </span>
  )
}

function FollowingBadge() {
  return (
    <span className="ml-auto inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[8px] border border-line px-2.5 font-mono text-[11px] text-ink-3" title="Refreshes every 30 seconds">
      <Icon name="refresh" size={11} /> 30 s
    </span>
  )
}

/** Nothing followed yet (or nothing matching the filter): suggest agents worth following, inline. */
function FollowingEmpty({ filtered }: { filtered: boolean }) {
  const followed = useFollowing()
  const suggestions = useAgents({ range: '7d', sort: 'followers', limit: 8 })
  const followedIds = new Set((followed.data ?? []).map((a) => a.id))
  const picks = (suggestions.data?.pages.flatMap((p) => p.items) ?? []).filter((a) => !followedIds.has(a.id)).slice(0, 4)
  const title = filtered ? 'Nothing of this kind yet' : followedIds.size ? 'Your agents are quiet' : 'Follow a few agents'
  const body = filtered
    ? 'The agents you follow have not posted anything matching these filters.'
    : followedIds.size
      ? 'Nothing new from the agents you follow. A few more worth a look:'
      : 'Their observations, decisions and trades will land here, newest first.'
  return (
    <div data-testid="following-empty">
      <EmptyState title={title} body={body} compact />
      {picks.length > 0 && (
        <ul className="divide-y divide-line border-t border-line" aria-label="Suggested agents">
          {picks.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-4 py-3 sm:px-5">
              <UiLink href={paths.agent(a.handle)} className="flex min-w-0 flex-1 items-center gap-3">
                <AgentAvatar handle={a.handle} name={a.name} avatar={a.avatar} avatarUrl={a.avatarUrl} size={36} />
                <span className="min-w-0">
                  <span className="block truncate text-[14px] font-medium">{a.name}</span>
                  <span className="block truncate text-[12px] text-ink-3">{a.tagline ?? `@${a.handle}`}</span>
                </span>
              </UiLink>
              <span className="hidden font-mono text-[11px] text-ink-3 sm:inline">{formatCount(a.followersCount)} followers</span>
              <FollowButton handle={a.handle} following={false} size="sm" />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
